/**
 * Detalhe de uma geladeira — summary, gráfico de temperaturas e
 * lista de devices físicos vinculados.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useVoltar } from "@/lib/voltar";
import {
  ArrowLeft,
  Building2,
  Cpu,
  DoorClosed,
  DoorOpen,
  Droplets,
  MapPin,
  Plug,
  Thermometer,
  Zap,
} from "lucide-react";
import clsx from "clsx";

import {
  useEquipment,
  useEquipmentReadings,
  useEquipmentStats,
  useEquipmentSummary,
} from "@/hooks/queries";

import { apiErrorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/Button";
import { Field, Select } from "@/components/Field";
import { DeviceHealthBadges } from "@/components/DeviceHealthBadges";
import { EquipmentAIInsightCard } from "@/components/EquipmentAIInsightCard";
import { EquipmentAlertsThresholdsTabs } from "@/components/EquipmentAlertsThresholdsTabs";
import { ChartStack } from "@/components/charts";
import {
  TabelaDeLeituras,
  granularidadeParaJanela,
} from "@/components/TabelaDeLeituras";
import type {
  AvailableSeries,
  ChartSeries,
  DoorEvent,
  MetricKey,
} from "@/components/charts";
import {
  formatPower,
  formatTemperature,
  roleLabel,
  shortId,
  timeAgo,
  toNumber,
} from "@/lib/format";
import {
  evaluateTemperature,
  statusLabel,
  statusTextColor,
  statusTone,
  type SetpointConfig,
} from "@/lib/thresholds";
import { Tip } from "@/components/Tip";

/**
 * Abaixo desta potência o fator de potência não descreve o compressor —
 * descreve a eletrônica de plantão. Um freezer parado puxa poucos watts, e
 * carga pequena com componente reativa dá FP baixíssimo por construção.
 */
const FP_CARGA_MINIMA_W = 30;

/** Medidor dentro de quadro fechado acima disso é assunto, não estatística. */
const MEDIDOR_QUENTE_C = 60;

// Bucket strings em formato canônico do Postgres (CAST AS interval).
// '1m' é ambíguo no Postgres (vira '1 month' em alguns casos); usar a forma
// completa evita surpresas.
//
// `custom` é um placeholder — fromHours/bucket são calculados dinamicamente
// a partir do range escolhido no date picker.
const RANGE_OPTIONS = [
  { value: "1h",     label: "Última 1h",    bucket: undefined as string | undefined, fromHours: 1 },
  { value: "3h",     label: "Últimas 3h",   bucket: "30 seconds" as string | undefined, fromHours: 3 },   // #174
  { value: "6h",     label: "Últimas 6h",   bucket: "1 minute"   as string | undefined, fromHours: 6 },
  { value: "12h",    label: "Últimas 12h",  bucket: "2 minutes"  as string | undefined, fromHours: 12 },  // #174
  { value: "24h",    label: "Últimas 24h",  bucket: "5 minutes"  as string | undefined, fromHours: 24 },
  { value: "7d",     label: "Últimos 7d",   bucket: "30 minutes" as string | undefined, fromHours: 24 * 7 },
  { value: "30d",    label: "Últimos 30d",  bucket: "2 hours"    as string | undefined, fromHours: 24 * 30 },
  { value: "custom", label: "Personalizado", bucket: undefined as string | undefined, fromHours: 0 },
];

// #174 Comparação temporal — desloca a mesma janela pra atrás no tempo.
// "none" = sem comparação (default). "d-1" = dia anterior. "d-7" = 7 dias atrás.
const COMPARE_OPTIONS = [
  { value: "none", label: "Sem comparação", offsetHours: 0 },
  { value: "d-1",  label: "Dia anterior",   offsetHours: 24 },
  { value: "d-7",  label: "7 dias atrás",   offsetHours: 24 * 7 },
] as const;
type CompareMode = typeof COMPARE_OPTIONS[number]["value"];

/** Escolhe bucket apropriado pra um range em horas — densidade de pontos
 *  alvo de ~500-2000 pontos por painel pra render eficiente do uPlot. */
function bucketForSpanHours(hours: number): string | undefined {
  if (hours <= 1)        return undefined;           // raw
  if (hours <= 3)        return "30 seconds";        // #174 ~360 pts
  if (hours <= 6)        return "1 minute";          // ~360 pts
  if (hours <= 12)       return "2 minutes";         // #174 ~360 pts
  if (hours <= 24)       return "5 minutes";         // ~288 pts
  if (hours <= 24 * 3)   return "10 minutes";        // ~432 pts
  if (hours <= 24 * 7)   return "30 minutes";        // ~336 pts
  if (hours <= 24 * 30)  return "2 hours";           // ~360 pts
  if (hours <= 24 * 90)  return "6 hours";           // ~360 pts
  return "1 day";                                    // long term
}

/**
 * #174 Aplica opacidade a uma cor hex — usado nas séries de comparação
 * pra ficarem visualmente subordinadas ao período atual.
 * Ex.: hexWithAlpha("#0891b2", 0.4) → "rgba(8, 145, 178, 0.4)"
 */
function hexWithAlpha(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Date local (UI input datetime-local) → ISO UTC pra mandar pro backend. */
function localDatetimeToIso(localValue: string): string {
  // datetime-local não tem timezone — assume local do browser, converte pra UTC.
  return new Date(localValue).toISOString();
}

/** ISO UTC → string "YYYY-MM-DDTHH:mm" pro datetime-local input. */
function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Paleta consistente entre painéis (mesma cor pra "Interna" em todos)
const SERIES_COLORS = {
  interna:  "#0891b2",
  produto:  "#10b981",
  externa:  "#94a3b8",
  tensao:   "#f59e0b",
  potencia: "#ef4444",
  corrente: "#a855f7",
  hum_int:  "#0ea5e9",
  hum_ext:  "#6366f1",
  fp:       "#14b8a6",
} as const;

export function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  // Volta de verdade quando há de onde; a lista é só o destino de quem
  // abriu esta tela direto. Ver `lib/voltar`.
  const voltar = useVoltar("/equipments");
  const equipment = useEquipment(id);
  const summary = useEquipmentSummary(id);

  const [rangeKey, setRangeKey] = useState("24h");
  const range = RANGE_OPTIONS.find((r) => r.value === rangeKey)!;

  // #174 Comparação temporal com dia anterior / 7 dias atrás
  const [compareMode, setCompareMode] = useState<CompareMode>("none");
  const compareOption = COMPARE_OPTIONS.find((o) => o.value === compareMode)!;
  const compareEnabled = compareMode !== "none";

  // Range customizado — só ativo quando rangeKey === "custom". Defaults:
  // últimos 7 dias (boa janela inicial pra explorar). Aplicado via botão
  // pra não disparar refetch a cada keystroke.
  const [customDraft, setCustomDraft] = useState(() => {
    const now = Date.now();
    return {
      from: isoToLocalDatetime(new Date(now - 7 * 24 * 3600 * 1000).toISOString()),
      to:   isoToLocalDatetime(new Date(now).toISOString()),
    };
  });
  const [customApplied, setCustomApplied] = useState<{ from: string; to: string } | null>(null);

  // Computa from/to/bucket ATIVOS baseados na seleção atual.
  const { fromIso, toIso, activeBucket } = useMemo(() => {
    if (rangeKey === "custom" && customApplied) {
      const fromMs = new Date(customApplied.from).getTime();
      const toMs   = new Date(customApplied.to).getTime();
      const spanHours = Math.max(0.1, (toMs - fromMs) / 3600_000);
      return {
        fromIso: new Date(fromMs).toISOString(),
        toIso:   new Date(toMs).toISOString(),
        activeBucket: bucketForSpanHours(spanHours),
      };
    }
    return {
      fromIso: new Date(Date.now() - range.fromHours * 3600 * 1000).toISOString(),
      toIso:   undefined as string | undefined,
      activeBucket: range.bucket,
    };
  }, [rangeKey, range, customApplied]);

  // #174 Janela deslocada pra comparação. Precisa `to` explícito porque o
  // período pode ser "24h" mas comparação é "24h de ontem", não "48h atrás
  // até agora".
  const { compareFromIso, compareToIso } = useMemo(() => {
    if (!compareEnabled) return { compareFromIso: undefined, compareToIso: undefined };
    const offsetMs = compareOption.offsetHours * 3600 * 1000;
    const fromMs = new Date(fromIso).getTime() - offsetMs;
    const toMsRef = toIso ? new Date(toIso).getTime() : Date.now();
    const toMs   = toMsRef - offsetMs;
    return {
      compareFromIso: new Date(fromMs).toISOString(),
      compareToIso:   new Date(toMs).toISOString(),
    };
  }, [compareEnabled, compareOption.offsetHours, fromIso, toIso]);

  function applyCustomRange() {
    if (!customDraft.from || !customDraft.to) return;
    if (new Date(customDraft.from) >= new Date(customDraft.to)) {
      // eslint-disable-next-line no-alert
      alert("A data inicial precisa ser anterior à final.");
      return;
    }
    setCustomApplied({ from: customDraft.from, to: customDraft.to });
  }

  // 3 séries de temperatura: interna, produto, externa.
  const internal = useEquipmentReadings(
    id,
    "temperature",
    { role: "temp_internal", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
  );
  const product = useEquipmentReadings(
    id,
    "temperature",
    { role: "temp_product", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const external = useEquipmentReadings(
    id,
    "temperature",
    { role: "temp_external", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );

  // ---------------------------------------------------------------------
  // Tabela de dados — consultas PRÓPRIAS, com bucket mais grosso.
  //
  // Não reaproveita as do gráfico de propósito: `activeBucket` mira 300–500
  // pontos (5 min em 24 h), o que na tabela vira 288 linhas. Consulta
  // separada custa uma ida ao backend, que cacheia por 60 s e agrega no
  // Postgres — barato perto de renderizar centenas de linhas que ninguém lê.
  const [granularidade, setGranularidade] = useState<string>("1 hour");
  useEffect(() => {
    // `toIso` é undefined nos presets ("últimas N horas") — ali o fim é
    // agora. Só o modo personalizado traz as duas pontas.
    const fim = toIso ? new Date(toIso).getTime() : Date.now();
    const horas = (fim - new Date(fromIso).getTime()) / 3_600_000;
    setGranularidade(granularidadeParaJanela(horas));
  }, [fromIso, toIso]);

  const tabelaInterna = useEquipmentReadings(
    id, "temperature",
    { role: "temp_internal", from: fromIso, to: toIso, bucket: granularidade, limit: 5000 },
    !!id,
  );
  const tabelaProduto = useEquipmentReadings(
    id, "temperature",
    { role: "temp_product", from: fromIso, to: toIso, bucket: granularidade, limit: 5000 },
    !!id,
  );

  // 3 séries de energia: tensão, corrente, potência.
  // Tasmota Sonoff publica 'rmsvoltage'/'rmscurrent'/'activepower'; o simulator
  // publica 'voltage'/'current'/'power'. Buscamos os dois nomes em paralelo
  // e usamos o que retornar dados (TanStack faz dedup automático).
  const voltageTasmota = useEquipmentReadings(
    id, "rmsvoltage",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const voltageSim = useEquipmentReadings(
    id, "voltage",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const currentTasmota = useEquipmentReadings(
    id, "rmscurrent",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const currentSim = useEquipmentReadings(
    id, "current",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const powerTasmota = useEquipmentReadings(
    id, "activepower",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const powerSim = useEquipmentReadings(
    id, "power",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );

  // Fator de potência — dois nomes pelo mesmo motivo de tensão/corrente:
  // TO-Q-SA1 publica `power_factor`, PJ-1203A publica `power_factor_a`.
  // `power_factor_b` fica de fora de propósito: no PJ-1203A o canal B só
  // existe se houver uma segunda garra, e sem ela o número é divisão por
  // zero disfarçada de medida.
  const fpTuya = useEquipmentReadings(
    id, "power_factor",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );
  const fpFaseA = useEquipmentReadings(
    id, "power_factor_a",
    { role: "power", from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );

  // Pega o que tiver dados (Tasmota OU simulator).
  // Tasmota reporta corrente em mA (raw ZCL), normalizamos pra A dividindo por 1000.
  const voltageData = (voltageTasmota.data?.length ? voltageTasmota.data : voltageSim.data) ?? [];
  const currentData = (
    currentTasmota.data?.length
      ? currentTasmota.data.map((p) => ({ ...p, value: (toNumber(p.value) ?? 0) / 1000 }))
      : currentSim.data
  ) ?? [];
  const powerData = (powerTasmota.data?.length ? powerTasmota.data : powerSim.data) ?? [];
  // Valor cru, em pontos percentuais (0–100), que é como os dois
  // conversores do z2m publicam. Não reescalamos aqui: se um fabricante
  // novo mandar 0–1, o erro aparece igual no tile e no gráfico, num lugar
  // só, em vez de duas heurísticas discordando em silêncio.
  const fpData = (fpTuya.data?.length ? fpTuya.data : fpFaseA.data) ?? [];

  // Umidade — pega de QUALQUER device do equipment que publique `humidity`
  // no payload (sem filtro de role). Esse é o caso típico do Zigbee2MQTT:
  // o sensor de temperatura também publica humidade no mesmo payload mas o
  // device está cadastrado com role `temp_internal` (e não `humidity_*`).
  // Sem o filtro de role, o backend agrega de todos os devices do ativo.
  const humAll = useEquipmentReadings(
    id, "humidity",
    { from: fromIso, to: toIso, bucket: activeBucket, limit: 5000 },
    !!id,
  );

  // #174 Queries de comparação — mesma janela deslocada. Só temperature +
  // humidity pra evitar 6 queries adicionais desnecessárias no default.
  // Se compareMode='none', enabled=false → 0 requests.
  const internalCmp = useEquipmentReadings(
    id, "temperature",
    { role: "temp_internal", from: compareFromIso, to: compareToIso, bucket: activeBucket, limit: 5000 },
    !!id && compareEnabled,
  );
  const productCmp = useEquipmentReadings(
    id, "temperature",
    { role: "temp_product", from: compareFromIso, to: compareToIso, bucket: activeBucket, limit: 5000 },
    !!id && compareEnabled,
  );
  const externalCmp = useEquipmentReadings(
    id, "temperature",
    { role: "temp_external", from: compareFromIso, to: compareToIso, bucket: activeBucket, limit: 5000 },
    !!id && compareEnabled,
  );
  const humCmp = useEquipmentReadings(
    id, "humidity",
    { from: compareFromIso, to: compareToIso, bucket: activeBucket, limit: 5000 },
    !!id && compareEnabled,
  );

  // Door: pega ambos os campos (Zigbee 'contact' invertido + simulator 'open').
  // SEM bucket pra preservar transições exatas (poucos pontos por natureza).
  const doorContact = useEquipmentReadings(
    id, "contact",
    { role: "door", from: fromIso, to: toIso, limit: 5000 },
    !!id,
  );
  const doorOpenRaw = useEquipmentReadings(
    id, "open",
    { role: "door", from: fromIso, to: toIso, limit: 5000 },
    !!id,
  );

  // ===== Memos derivados — DEVEM rodar antes de qualquer early return
  //       (Rules of Hooks: mesma quantidade de hooks por render). =====

  // Alinha todas as séries no mesmo eixo X (epoch seconds).
  // #174 Realinha timestamps das séries de comparação → soma offset pra
  // alinhar no mesmo eixo X do período atual. Sem isso, uPlot renderiza
  // em pontos completamente diferentes do eixo.
  const shiftedCmpData = useMemo(() => {
    if (!compareEnabled) return { interna: [], produto: [], externa: [], humidade: [] };
    const offsetMs = compareOption.offsetHours * 3600 * 1000;
    const shift = (arr: SeriesPoint[] | undefined) =>
      (arr ?? []).map((p) => ({
        time:  new Date(new Date(p.time).getTime() + offsetMs).toISOString(),
        value: p.value,
      }));
    return {
      interna:  shift(internalCmp.data),
      produto:  shift(productCmp.data),
      externa:  shift(externalCmp.data),
      humidade: shift(humCmp.data),
    };
  }, [
    compareEnabled, compareOption.offsetHours,
    internalCmp.data, productCmp.data, externalCmp.data, humCmp.data,
  ]);

  const chartBundle = useMemo(() => {
    return buildChartBundle({
      interna:  internal.data ?? [],
      produto:  product.data ?? [],
      externa:  external.data ?? [],
      tensao:   voltageData,
      potencia: powerData,
      corrente: currentData,
      fp:       fpData,
      humidade: humAll.data ?? [],
      // #174 Séries de comparação — sufixo _cmp pra distinguir no buildBundle
      interna_cmp:  shiftedCmpData.interna,
      produto_cmp:  shiftedCmpData.produto,
      externa_cmp:  shiftedCmpData.externa,
      humidade_cmp: shiftedCmpData.humidade,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    internal.data, product.data, external.data,
    voltageData, powerData, currentData, fpData,
    humAll.data,
    shiftedCmpData,
  ]);

  // Door events — inverte 'contact' (Zigbee: true=fechado), usa 'open' direto.
  const doorEvents = useMemo<DoorEvent[]>(() => {
    const contactReadings = (doorContact.data ?? []).map((p) => ({
      time: p.time,
      value: invertContact(toNumber(p.value)),
    }));
    const openReadings = (doorOpenRaw.data ?? []).map((p) => ({
      time: p.time,
      value: toNumber(p.value),
    }));
    const merged = [...contactReadings, ...openReadings]
      .filter((p) => p.value !== null)
      .sort((a, b) => a.time.localeCompare(b.time));
    return computeDoorEvents(merged);
  }, [doorContact.data, doorOpenRaw.data]);

  // Series por métrica pra cada painel possível.
  // O modo "custom" não vai aqui — ele é resolvido no ChartStack a partir
  // de `availableSeries` + IDs selecionados.
  const seriesByMetric = useMemo<Record<Exclude<MetricKey, "custom">, ChartSeries[]>>(() => {
    // #174 helper: cria série de comparação com dash + cor opaca.
    // Sufixo no label deixa claro qual é qual na legenda (ex: "Interna (D-1)").
    const cmpSuffix = compareMode === "d-1" ? "D-1" : compareMode === "d-7" ? "D-7" : "";
    const mkCmp = (label: string, values: (number | null)[], color: string, width = 1.5): ChartSeries | null => {
      const s = seriesOrNull(`${label} (${cmpSuffix})`, values, hexWithAlpha(color, 0.5), width);
      if (!s) return null;
      return { ...s, dash: [6, 4] };
    };

    const tempSeries: (ChartSeries | null)[] = [
      seriesOrNull("Interna", chartBundle.values.interna, SERIES_COLORS.interna, 2),
      seriesOrNull("Produto", chartBundle.values.produto, SERIES_COLORS.produto, 2),
      seriesOrNull("Externa", chartBundle.values.externa, SERIES_COLORS.externa, 1.5),
    ];
    const humSeries: (ChartSeries | null)[] = [
      seriesOrNull("Umidade", chartBundle.values.humidade, SERIES_COLORS.hum_int, 2),
    ];
    if (compareEnabled) {
      tempSeries.push(
        mkCmp("Interna", chartBundle.values.interna_cmp, SERIES_COLORS.interna, 1.5),
        mkCmp("Produto", chartBundle.values.produto_cmp, SERIES_COLORS.produto, 1.5),
        mkCmp("Externa", chartBundle.values.externa_cmp, SERIES_COLORS.externa, 1.5),
      );
      humSeries.push(
        mkCmp("Umidade", chartBundle.values.humidade_cmp, SERIES_COLORS.hum_int, 1.5),
      );
    }

    return {
      temperature: tempSeries.filter((s): s is ChartSeries => s !== null),
      energy: [
        seriesOrNull("Potência (W)", chartBundle.values.potencia, SERIES_COLORS.potencia, 2),
        seriesOrNull("Tensão (V)",   chartBundle.values.tensao,   SERIES_COLORS.tensao,   1.5),
        seriesOrNull("Corrente (A)", chartBundle.values.corrente, SERIES_COLORS.corrente, 1.5, 1),
      ].filter((s): s is ChartSeries => s !== null),
      humidity: humSeries.filter((s): s is ChartSeries => s !== null),
    };
  }, [chartBundle, compareEnabled, compareMode]);

  // Catálogo completo de séries pra modo "Personalizado" — user pode escolher
  // qualquer combinação (ex: potência + temperatura interna juntos).
  // Filtra automaticamente as que não têm dados no período.
  const availableSeries = useMemo<AvailableSeries[]>(() => {
    const all: AvailableSeries[] = [
      { id: "interna",  label: "Temp. interna",  unit: "°C", color: SERIES_COLORS.interna,  width: 2,   values: chartBundle.values.interna  ?? [] },
      { id: "produto",  label: "Temp. produto",  unit: "°C", color: SERIES_COLORS.produto,  width: 2,   values: chartBundle.values.produto  ?? [] },
      { id: "externa",  label: "Temp. externa",  unit: "°C", color: SERIES_COLORS.externa,  width: 1.5, values: chartBundle.values.externa  ?? [] },
      { id: "potencia", label: "Potência",       unit: "W",  color: SERIES_COLORS.potencia, width: 2,   values: chartBundle.values.potencia ?? [] },
      { id: "tensao",   label: "Tensão",         unit: "V",  color: SERIES_COLORS.tensao,   width: 1.5, values: chartBundle.values.tensao   ?? [] },
      { id: "corrente", label: "Corrente",       unit: "A",  color: SERIES_COLORS.corrente, width: 1.5, values: chartBundle.values.corrente ?? [] },
      // Fora dos painéis prontos de propósito: no painel de Energia ele
      // dividiria o eixo com os watts e viraria uma reta rente ao chão.
      // Aqui, ao lado da potência, é onde ele diz alguma coisa — o FP sobe
      // quando o compressor entra, e o platô é a única leitura honesta dele.
      { id: "fp",       label: "Fator de potência", unit: "%", color: SERIES_COLORS.fp,       width: 1.5, values: chartBundle.values.fp       ?? [] },
      { id: "umidade",  label: "Umidade",        unit: "%",  color: SERIES_COLORS.hum_int,  width: 2,   values: chartBundle.values.humidade ?? [] },
    ];
    // Só séries com pelo menos 1 valor não-null
    return all.filter((s) => s.values.some((v) => v !== null));
  }, [chartBundle]);

  const chartLoading =
    internal.isLoading ||
    product.isLoading ||
    external.isLoading;

  // Stats agregados do período (mesmo range dos gráficos)
  const statsQuery = useEquipmentStats(id, { from: fromIso, to: toIso });

  // ===== Early returns só DEPOIS dos hooks. =====

  if (equipment.isLoading || summary.isLoading) return <Loading />;
  if (!equipment.data || !summary.data) {
    return (
      <div className="mx-auto max-w-2xl">
        <button
          type="button"
          onClick={voltar}
          className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        <p className="mt-4 text-slate-600 dark:text-slate-400">Ativo não encontrado.</p>
      </div>
    );
  }

  const eq = equipment.data;
  const sum = summary.data;
  const cfg = (eq.metadata ?? {}) as SetpointConfig;

  const primaryTemp =
    sum.metrics.temp_internal ??
    sum.metrics.temp_product ??
    null;

  const eval_ = evaluateTemperature(primaryTemp, cfg, sum.last_seen);

  const productTemp = sum.metrics.temp_product ?? null;
  const externalTemp = sum.metrics.temp_external ?? null;

  // Umidade — pode vir de 3 fontes (em ordem de prioridade):
  //   1. Device dedicado role=humidity_internal      → metrics.humidity_internal
  //   2. Junto com a temperatura interna (DHT/SHT)   → metrics.temp_internal_humidity
  //   3. Tasmota all-in-one (sem role)               → metrics.INTERNAL_humidity ou .humidity
  const humidityInternal =
    sum.metrics.humidity_internal ??
    sum.metrics.temp_internal_humidity ??
    sum.metrics.INTERNAL_humidity ??
    sum.metrics.humidity ??
    null;
  const humidityExternal =
    sum.metrics.humidity_external ??
    sum.metrics.temp_external_humidity ??
    sum.metrics.EXTERNAL_humidity ??
    null;

  const power = sum.metrics.power ?? null;
  const voltage = sum.metrics.power_rmsvoltage ?? sum.metrics.power_voltage ?? null;
  // Tasmota ZCL reporta RMSCurrent em mA (raw cluster value), simulator em A.
  // Detecta pelo campo de origem e divide quando necessário.
  const currentRaw = sum.metrics.power_rmscurrent ?? sum.metrics.power_current ?? null;
  const isTasmotaCurrent = sum.metrics.power_rmscurrent != null;
  const current = currentRaw != null
    ? (toNumber(currentRaw) ?? 0) / (isTasmotaCurrent ? 1000 : 1)
    : null;
  // Nome canônico: a tradução de fabricante (power_factor / power_factor_a)
  // é resolvida no backend, em `campos.py`, e não espalhada aqui.
  const powerFactor = toNumber(sum.metrics.power_factor ?? null);
  const meterTemp = toNumber(sum.metrics.meter_temperature ?? null);
  // Fator de potência SÓ se lê com carga. Com o compressor em descanso sobra
  // a eletrônica puxando poucos watts, e o FP despenca. Sem esta marca o
  // número induz a procurar problema onde não há.
  //
  // O que a ajuda da tela deliberadamente NÃO afirma é uma faixa saudável.
  // Ela já dizia "entre 80% e 95% com o compressor rodando", e a medição
  // desmentiu: em 10/09/26, este TO-Q-SA1 com o compressor rodando estável
  // a 122 W (1,96 A × 131 V = 257 VA aparentes) reportou FP de 47%. Não é
  // erro do medidor — compressor hermético monofásico pequeno tem FP baixo
  // por construção. Qualquer faixa que a gente publique é a de UM
  // equipamento e a mentira de todos os outros.
  const powerNum = toNumber(power);
  const cargaBaixa = powerNum !== null && powerNum < FP_CARGA_MINIMA_W;
  // Temperatura do medidor é dado de manutenção: só ocupa espaço quando
  // pede ação. Abaixo do limiar ela vive na ajuda.
  const medidorQuente = meterTemp !== null && meterTemp >= MEDIDOR_QUENTE_C;
  const door = sum.metrics.door ?? null;
  const doorOpen = toNumber(door) === 1;

  return (
    <div className="mx-auto max-w-7xl">
      {/* Crumbs (linha fina): Tenant · Site */}
      {(sum.tenant_name || sum.site_name) && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          {sum.tenant_name && (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3 text-slate-400 dark:text-slate-500" />
              {sum.tenant_name}
            </span>
          )}
          {sum.tenant_name && sum.site_name && (
            <span className="text-slate-300">·</span>
          )}
          {sum.site_name && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3 text-slate-400 dark:text-slate-500" />
              {sum.site_name}
            </span>
          )}
        </div>
      )}

      <PageHeader
        voltar
        // Quem chega por marcador da planta volta pra planta; só quem abriu
        // esta tela direto (link colado, favorito) cai na lista.
        voltarPara="/equipments"
        title={eq.name}
        description={
          <span className="inline-flex items-center gap-2">
            <Badge tone="neutral">{eq.kind ?? "—"}</Badge>
            <span>slug: <code className="text-slate-700 dark:text-slate-300">{eq.slug}</code></span>
            <span>· id: <code>{shortId(eq.id)}</code></span>
          </span>
        }
        actions={
          sum.archived
            ? <Badge tone="neutral">Arquivada</Badge>
            : <Badge tone={statusTone(eval_.status)}>{statusLabel(eval_.status)}</Badge>
        }
      />

      {/* Banner de arquivamento — mostra ao topo pra deixar claro que o resto
          da tela é histórico, não estado atual. */}
      {sum.archived && (
        <div className="mt-4 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100/70 dark:bg-slate-800/70 p-4">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Ativo arquivado
          </div>
          <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            {sum.archived_at
              ? `Arquivada há ${timeAgo(sum.archived_at)}. `
              : "Marcada como arquivada. "}
            Métricas, gráficos e análise de AI abaixo refletem histórico e não
            representam estado atual. Alertas não são disparados pra esse ativo.
            Pra reativar, edite em Admin → Ativos.
          </div>
        </div>
      )}

      {/* Top metrics — só renderiza tiles que têm valor (respeita N sensores).
          Grid lg:grid-cols-6 pra caber: interna, produto, externa, umidade,
          porta, energia. Tiles ausentes (ex: sem sensor produto) simplesmente
          não aparecem e o grid se reorganiza. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {primaryTemp !== null && (
          <MetricTile
            icon={<Thermometer className="h-4 w-4" />}
            label="Temp. interna"
            value={formatTemperature(primaryTemp)}
            valueClassName={statusTextColor(eval_.status)}
            hint={
              cfg.setpoint_min != null && cfg.setpoint_max != null
                ? `range: ${cfg.setpoint_min} a ${cfg.setpoint_max} °C`
                : "sem range definido"
            }
          />
        )}
        {productTemp !== null && (
          <MetricTile
            icon={<Thermometer className="h-4 w-4" />}
            label="Temp. produto"
            value={formatTemperature(productTemp)}
          />
        )}
        {externalTemp !== null && (
          <MetricTile
            icon={<Thermometer className="h-4 w-4" />}
            label="Temp. externa"
            value={formatTemperature(externalTemp)}
          />
        )}
        {humidityInternal !== null && (
          <MetricTile
            icon={<Droplets className="h-4 w-4 text-sky-500" />}
            label="Umidade"
            value={`${toNumber(humidityInternal)?.toFixed(0) ?? "—"}%`}
            hint={
              humidityExternal !== null
                ? `externa: ${toNumber(humidityExternal)?.toFixed(0)}%`
                : undefined
            }
          />
        )}
        <MetricTile
          icon={doorOpen ? <DoorOpen className="h-4 w-4 text-amber-600" /> : <DoorClosed className="h-4 w-4 text-emerald-600" />}
          label="Porta"
          value={
            <span className={doorOpen ? "text-amber-700" : "text-emerald-700"}>
              {doorOpen ? "Aberta" : "Fechada"}
            </span>
          }
        />
        <MetricTile
          icon={<Zap className="h-4 w-4 text-amber-500" />}
          label="Energia"
          value={
            power !== null || voltage !== null || current !== null ? (
              <div className="flex flex-col gap-0.5">
                {power !== null && (
                  <span className="flex items-center gap-1 text-base text-slate-800 dark:text-slate-200">
                    <Plug className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                    {formatPower(power)}
                  </span>
                )}
                {(voltage !== null || current !== null || powerFactor !== null) && (
                  <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                    {voltage !== null && `${toNumber(voltage)?.toFixed(0) ?? "—"} V`}
                    {voltage !== null && current !== null && " · "}
                    {current !== null && `${(toNumber(current) ?? 0).toFixed(2)} A`}
                    {powerFactor !== null && (voltage !== null || current !== null) && " · "}
                    {powerFactor !== null && (
                      // Apagado quando não há carga: o número continua visível
                      // pra quem quiser, sem competir por atenção com os que
                      // descrevem alguma coisa.
                      <span className={cargaBaixa ? "opacity-50" : undefined}>
                        FP {powerFactor.toFixed(0)}%
                      </span>
                    )}
                    {(powerFactor !== null || meterTemp !== null) && (
                      <Tip label="Sobre fator de potência e medidor">
                        <span className="block">
                          <b>FP</b> é o fator de potência. Só descreve o
                          compressor quando há carga — em descanso ele cai
                          sozinho, sem defeito.
                        </span>
                        {meterTemp !== null && (
                          <span className="mt-2 block">
                            <b>Medidor a {meterTemp.toFixed(0)} °C</b> —
                            temperatura do próprio aparelho, não do interior.
                          </span>
                        )}
                      </Tip>
                    )}
                  </span>
                )}
                {medidorQuente && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    medidor a {meterTemp!.toFixed(0)} °C
                  </span>
                )}
              </div>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">—</span>
            )
          }
        />
      </div>

      {/* Indicadores AGREGADOS do período (kWh, R$, SLI/SLO) — usa o
          mesmo range do gráfico via fromIso/toIso. */}
      <PeriodIndicators stats={statsQuery.data} loading={statsQuery.isLoading}
                        erro={statsQuery.error} rangeLabel={range.label} />

      {/* envpulse AI — insights computados pelo analytics worker (a cada 6h).
          Janela fixa de 7 dias vs baseline de 30d anteriores (não usa o range
          do histórico, é uma análise de período fechado pra detectar desvios
          estruturais).
          Pra ativo arquivado: NÃO renderiza — backend retorna 404 e o card
          antigo daria falsa confiança ("tudo nos eixos" com zero dado atual). */}
      {!sum.archived && <EquipmentAIInsightCard equipmentId={eq.id} />}

      {/* Charts (uPlot) — N painéis empilhados com cursor sincronizado.
          Cada painel mostra uma métrica (temp/energia/umidade) e pode ativar
          overlay de porta. Layout persiste em localStorage por equipment. */}
      <Card className="mt-6">
        <CardHeader
          title="Histórico"
          subtitle={
            <span>
              Última atualização {timeAgo(sum.last_seen)} —{" "}
              <span className="text-slate-400 dark:text-slate-500">
                arraste o cursor pra sincronizar entre painéis
              </span>
            </span>
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Field className="w-44">
                <Select
                  value={rangeKey}
                  onChange={(e) => setRangeKey(e.target.value)}
                  options={RANGE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
                />
              </Field>
              {/* #174 Comparação temporal — só faz sentido pra períodos <= 24h
                  (comparar 7d contra 7d de 7 dias atrás fica confuso visualmente
                  e ainda dobra requests). Escondido em ranges maiores. */}
              {(rangeKey === "1h" || rangeKey === "3h" || rangeKey === "6h" ||
                rangeKey === "12h" || rangeKey === "24h") && (
                <Field className="w-48">
                  <Select
                    value={compareMode}
                    onChange={(e) => setCompareMode(e.target.value as CompareMode)}
                    options={COMPARE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </Field>
              )}
              {rangeKey === "custom" && (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col text-[11px] text-slate-500 dark:text-slate-400">
                    De
                    <input
                      type="datetime-local"
                      value={customDraft.from}
                      onChange={(e) => setCustomDraft((d) => ({ ...d, from: e.target.value }))}
                      className="rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                    />
                  </label>
                  <label className="flex flex-col text-[11px] text-slate-500 dark:text-slate-400">
                    Até
                    <input
                      type="datetime-local"
                      value={customDraft.to}
                      onChange={(e) => setCustomDraft((d) => ({ ...d, to: e.target.value }))}
                      className="rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                    />
                  </label>
                  <Button size="sm" variant="secondary" onClick={applyCustomRange}>
                    Aplicar
                  </Button>
                </div>
              )}
            </div>
          }
        />
        <CardBody className="pt-3">
          <ChartStack
            equipmentId={eq.id}
            timestamps={chartBundle.timestamps}
            seriesByMetric={seriesByMetric}
            availableSeries={availableSeries}
            doorEvents={doorEvents}
            loading={chartLoading}
          />
        </CardBody>
      </Card>

      {/* Tabela dos mesmos dados. Fechada por padrão: quem abre a tela quer
          ver a forma da curva; a tabela é pra quem precisa de número. */}
      <Card className="mt-6">
        <CardBody>
          <TabelaDeLeituras
            granularidade={granularidade}
            onGranularidade={setGranularidade}
            carregando={tabelaInterna.isLoading || tabelaProduto.isLoading}
            nomeBase={eq.slug || "leituras"}
            series={[
              { rotulo: "Interna", unidade: "°C", dados: tabelaInterna.data },
              { rotulo: "Produto", unidade: "°C", dados: tabelaProduto.data },
            ]}
          />
        </CardBody>
      </Card>

      {/* Abas Alertas + Thresholds — antes eram empilhados; separados agora
          pra reduzir ruído (alertas = reativo, thresholds = configuração).
          Aba ativa persiste no ?tab= da URL. */}
      {sum.tenant_id && (
        <EquipmentAlertsThresholdsTabs
          equipmentId={eq.id}
          tenantId={sum.tenant_id}
        />
      )}

      {/* Devices */}
      <Card className="mt-6">
        <CardHeader
          title="Devices vinculados"
          subtitle={`${sum.devices.length} hardware${sum.devices.length === 1 ? "" : "s"} físico${sum.devices.length === 1 ? "" : "s"}`}
        />
        <CardBody>
          {sum.devices.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum device atribuído a esse ativo.
            </div>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {sum.devices.map((d) => {
                const status = sum.device_statuses[d.id] ?? null;
                return (
                  <li
                    key={d.id}
                    className="flex items-center justify-between rounded-lg border border-surface-200 dark:border-slate-800 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                      <div>
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {d.name}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {roleLabel(d.role)} · {d.local_id}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DeviceHealthBadges items={[{ device: d, status }]} mode="expanded" />
                      <Link to={`/admin/devices?focus=${d.id}`}>
                        <Button size="sm" variant="ghost">Detalhes</Button>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------- helpers locais ---------------------------------------------------

interface SeriesPoint {
  time: string;
  value: number | string | null;
}

interface ChartBundle {
  /** Eixo X compartilhado (epoch seconds). */
  timestamps: number[];
  /** Valores Y por série, alinhados a `timestamps` (null = sem leitura). */
  values: Record<string, (number | null)[]>;
}

/** ISO → epoch seconds (sem milissegundos pra alinhamento estável). */
function isoToEpochSec(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000);
}

/**
 * Alinha N séries no MESMO eixo X (união de timestamps). Para cada timestamp,
 * cada série recebe o valor exato lido naquele instante, ou `null` se não houver.
 *
 * Não interpola — uPlot conecta gaps com `spanGaps:false`, então gaps reais
 * aparecem como espaços vazios (correto pra visualização honesta).
 */
function buildChartBundle(series: Record<string, SeriesPoint[]>): ChartBundle {
  const tsSet = new Set<number>();
  // Maps de cada série: ts → valor numérico
  const seriesMaps: Record<string, Map<number, number | null>> = {};

  for (const [name, arr] of Object.entries(series)) {
    const m = new Map<number, number | null>();
    for (const p of arr) {
      const ts = isoToEpochSec(p.time);
      const n = toNumber(p.value);
      m.set(ts, n);
      tsSet.add(ts);
    }
    seriesMaps[name] = m;
  }

  const timestamps = Array.from(tsSet).sort((a, b) => a - b);
  const values: Record<string, (number | null)[]> = {};
  for (const [name, m] of Object.entries(seriesMaps)) {
    values[name] = timestamps.map((ts) => (m.has(ts) ? m.get(ts)! : null));
  }
  return { timestamps, values };
}

/**
 * Constrói uma ChartSeries — retorna null se a série está vazia (sem valores
 * não-null), pra evitar adicionar "ruído" no chart.
 */
function seriesOrNull(
  label: string,
  values: (number | null)[] | undefined,
  color: string,
  width: number,
  axis: 0 | 1 = 0,
): ChartSeries | null {
  if (!values || !values.some((v) => v !== null)) return null;
  return { label, values, color, width, axis };
}

/** Inverte semântica do Zigbee contact (1=fechado → 0=fechado, etc). */
function invertContact(v: number | null): number | null {
  if (v == null) return null;
  return v === 0 ? 1 : 0;
}

/**
 * Detecta transições 0↔1 na série de porta e gera DoorEvents pra overlay.
 * Convenção (já normalizada): 1 = porta aberta, 0 = porta fechada.
 * Pontos com value=null são ignorados.
 */
function computeDoorEvents(readings: Array<{ time: string; value: number | null }>): DoorEvent[] {
  const events: DoorEvent[] = [];
  let openedAt: number | null = null;
  let prevState: number | null = null;

  for (const r of readings) {
    if (r.value == null) continue;
    const state = r.value === 1 ? 1 : 0;
    const ts = isoToEpochSec(r.time);

    if (prevState === null) {
      // Estado inicial — se já entra aberto, abre evento desde o primeiro ponto
      if (state === 1) openedAt = ts;
    } else if (prevState === 0 && state === 1) {
      openedAt = ts;
    } else if (prevState === 1 && state === 0 && openedAt !== null) {
      events.push({ openedAt, closedAt: ts });
      openedAt = null;
    }
    prevState = state;
  }

  // Se ainda está aberto no fim, fecha em "agora" (closedAt = null pra MetricChart)
  if (openedAt !== null) {
    events.push({ openedAt, closedAt: null });
  }
  return events;
}

function MetricTile({
  icon,
  label,
  value,
  valueClassName,
  hint,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-card">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {icon}
        {label}
      </div>
      <div className={clsx("mt-2 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100", valueClassName)}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

// ============================================================================
// PeriodIndicators — segunda fileira de tiles agregando dados do período
// (kWh, R$, SLI/SLO). Vem de useEquipmentStats no parent.
// ============================================================================

function PeriodIndicators({
  stats,
  loading,
  erro,
  rangeLabel,
}: {
  stats: ReturnType<typeof useEquipmentStats>["data"];
  loading: boolean;
  erro?: unknown;
  rangeLabel: string;
}) {
  // `loading || !stats` tratava DUAS situações como uma.
  //
  // Enquanto carrega, esqueleto é o certo. Quando a consulta FALHA, `loading`
  // volta a ser false e `stats` continua indefinido — e a condição antiga
  // deixava o esqueleto na tela para sempre. Três retângulos cinza parados,
  // sem erro, sem explicação e sem sugerir o que fazer: o mesmo desfecho do
  // modal em branco de 17/09, com outra cara.
  if (loading) {
    return (
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-20 rounded-xl bg-surface-100 dark:bg-slate-800/40 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm
                      text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30
                      dark:text-amber-200">
        <p className="font-medium">
          Não consegui carregar os indicadores do período.
        </p>
        <p className="mt-0.5 text-xs opacity-90">
          {erro ? apiErrorMessage(erro) : "A consulta não retornou dados."}{" "}
          O resto da página continua válido.
        </p>
      </div>
    );
  }

  const { energy, temperature } = stats;
  const kwhTxt =
    energy.total_kwh < 10
      ? energy.total_kwh.toFixed(2)
      : energy.total_kwh.toFixed(1);
  const costTxt = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(energy.estimated_cost);
  // Só avisa quando há o que avisar: cobertura cheia não vira ruído no cartão.
  const cobertura = energy.coverage_pct;
  const parcial = cobertura !== null && cobertura < 99;

  const sli = temperature.sli_pct;
  const slo = temperature.slo_target_pct;
  const sliColor =
    sli === null
      ? "text-slate-400 dark:text-slate-500"
      : sli >= slo
      ? "text-emerald-600 dark:text-emerald-400"
      : sli >= 95
      ? "text-amber-600 dark:text-amber-400"
      : "text-red-600 dark:text-red-400";

  return (
    <div className="mt-3">
      <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Indicadores do período · {rangeLabel.toLowerCase()}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* kWh */}
        <div className="rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-card">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <Zap className="h-4 w-4 text-amber-500" />
            Consumo
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
            {kwhTxt}{" "}
            <span className="text-base font-medium text-slate-500 dark:text-slate-400">kWh</span>
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {energy.avg_power_w !== null
              ? `~${Math.round(energy.avg_power_w)} W médio`
              : "sem leitura"}
          </div>
          {parcial && (
            <div className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              {cobertura!.toFixed(0)}% do período medido
              <Tip label="O que é cobertura da medição">
                Intervalos maiores que 10 minutos entre leituras não entram na
                conta — sem dado não há consumo a somar. Então este total é do
                que foi medido, não do período inteiro. Cobertura baixa
                costuma ser sensor fora do ar, não geladeira parada.
              </Tip>
            </div>
          )}
        </div>

        {/* R$ */}
        <div className="rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-card">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <Plug className="h-4 w-4 text-emerald-500" />
            Custo estimado
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
            {costTxt}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            tarifa R$ {energy.tariff_per_kwh.toFixed(2)}/kWh
          </div>
          {parcial && (
            <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              sobre {cobertura!.toFixed(0)}% do período
            </div>
          )}
        </div>

        {/* SLI / SLO */}
        <div className="rounded-xl border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-card">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <Thermometer className="h-4 w-4 text-cyan-600" />
            SLI / SLO
          </div>
          <div className={clsx("mt-2 text-2xl font-bold tabular-nums", sliColor)}>
            {sli !== null ? `${sli.toFixed(1)}%` : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {sli !== null
              ? `meta ${slo.toFixed(0)}% · ${Math.round(temperature.out_of_range_minutes)} min fora`
              : "sem setpoint configurado"}
          </div>
        </div>
      </div>
    </div>
  );
}
